import React, { useState, useMemo } from 'react';
import { 
  HelpCircle, Search, ChevronDown, ChevronUp, 
  Briefcase, CheckSquare, Award, QrCode, GraduationCap, 
  Sparkles, ArrowRight, ShieldCheck, MessageSquare, X,
  Layers
} from 'lucide-react';
import { Language, FaqCategory } from '../types';
import { translations } from '../i18n/translations';
import { faqData } from '../data/faqData';

interface FAQSectionProps {
  currentLang: Language;
  onNavigateToContact?: (subject?: string) => void;
  onNavigateToVerify?: () => void;
}

export const FAQSection: React.FC<FAQSectionProps> = ({
  currentLang,
  onNavigateToContact,
  onNavigateToVerify,
}) => {
  const t = translations[currentLang];
  const [selectedCategory, setSelectedCategory] = useState<FaqCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({
    'consulting-steps': true,
    'audit-types': true,
    'verify-how-it-works': true,
  });

  const categories: { id: FaqCategory; label: string; icon: React.ElementType }[] = [
    { id: 'all', label: t.faq.categories.all, icon: Layers },
    { id: 'consulting', label: t.faq.categories.consulting, icon: Briefcase },
    { id: 'audit', label: t.faq.categories.audit, icon: CheckSquare },
    { id: 'certification', label: t.faq.categories.certification, icon: Award },
    { id: 'verification', label: t.faq.categories.verification, icon: QrCode },
    { id: 'training', label: t.faq.categories.training, icon: GraduationCap },
  ];

  // Filter FAQ items based on category and search query
  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      // Category filter
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      if (!matchesCategory) return false;

      // Search query filter (matches in active language, English, or French for broad coverage)
      if (!searchQuery.trim()) return true;
      
      const query = searchQuery.toLowerCase().trim();
      const qText = (item.question?.[currentLang] || item.question?.fr || '').toLowerCase();
      const aText = (item.answer?.[currentLang] || item.answer?.fr || '').toLowerCase();
      const tags = (item.tags || []).join(' ').toLowerCase();

      return qText.includes(query) || aText.includes(query) || tags.includes(query);
    });
  }, [selectedCategory, searchQuery, currentLang]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: faqData.length };
    faqData.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  }, []);

  const toggleItem = (id: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleExpandAll = () => {
    const allOpen: Record<string, boolean> = {};
    filteredFaqs.forEach((faq) => {
      allOpen[faq.id] = true;
    });
    setOpenItems(allOpen);
  };

  const handleCollapseAll = () => {
    setOpenItems({});
  };

  const getCategoryLabel = (category: string) => {
    return t.faq.categories[category as keyof typeof t.faq.categories] || category;
  };

  return (
    <section id="faq" className="py-20 lg:py-28 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white transition-colors duration-200 relative overflow-hidden">
      
      <div className="w-[90%] max-w-none mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center w-[90%] lg:w-[70%] max-w-none mx-auto space-y-4 mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 dark:bg-cyan-500/20 border border-cyan-500/30 text-cyan-800 dark:text-cyan-300 text-xs font-bold tracking-wide uppercase shadow-sm">
            <HelpCircle className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
            <span>{t.faq.badge}</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold font-montserrat tracking-tight text-slate-900 dark:text-white">
            {t.faq.title}
          </h2>

          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
            {t.faq.subtitle}
          </p>
        </div>

        {/* Search Bar & Controls */}
        <div className="w-[90%] lg:w-[70%] max-w-none mx-auto mb-8 space-y-4">
          <div className="relative">
            <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none text-slate-400">
              <Search className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.faq.searchPlaceholder}
              className="w-full ps-11 pe-10 py-3.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-sm text-xs sm:text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-slate-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 end-0 pe-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                title={currentLang === 'ar' ? 'مسح' : currentLang === 'en' ? 'Clear' : 'Effacer'}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Expand / Collapse Controls */}
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-1">
            <span>
              {currentLang === 'ar'
                ? `${filteredFaqs.length} أسئلة متوفرة`
                : currentLang === 'en'
                ? `${filteredFaqs.length} ${filteredFaqs.length > 1 ? 'questions found' : 'question found'}`
                : `${filteredFaqs.length} ${filteredFaqs.length > 1 ? 'questions trouvées' : 'question trouvée'}`}
            </span>

            <div className="flex items-center gap-3 font-semibold">
              <button
                onClick={handleExpandAll}
                className="hover:text-cyan-600 dark:hover:text-cyan-300 transition-colors flex items-center gap-1"
              >
                <ChevronDown className="w-3.5 h-3.5" />
                <span>{t.faq.expandAll}</span>
              </button>
              <span>•</span>
              <button
                onClick={handleCollapseAll}
                className="hover:text-cyan-600 dark:hover:text-cyan-300 transition-colors flex items-center gap-1"
              >
                <ChevronUp className="w-3.5 h-3.5" />
                <span>{t.faq.collapseAll}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Category Pills Filter */}
        <div className="flex flex-wrap items-center justify-center gap-2 pb-4 mb-10">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isSelected = selectedCategory === cat.id;
            const count = categoryCounts[cat.id] || 0;

            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-2 rounded-sm text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all shrink-0 ${
                  isSelected
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold'
                    : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-white dark:text-slate-900' : 'text-slate-700 dark:text-slate-300'}`} />
                <span>{cat.label}</span>
                <span className={`px-1.5 py-0.2 rounded-sm text-[10px] ${
                  isSelected 
                    ? 'bg-white/20 text-white dark:text-slate-900 dark:bg-slate-900/10' 
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* FAQ Accordion List */}
        {filteredFaqs.length === 0 ? (
          <div className="w-[90%] lg:w-[60%] max-w-none mx-auto text-center py-12 px-6 rounded-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4">
            <div className="w-12 h-12 mx-auto flex items-center justify-center">
              <Search className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-base font-bold text-slate-800 dark:text-white">
              {t.faq.noResults}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {currentLang === 'ar'
                ? 'جرب كلمة بحث أخرى أو أعد ضبط الفلاتر لعرض جميع الإجابات.'
                : currentLang === 'en'
                ? 'Try another keyword or reset filters to display all answers.'
                : "Essayez un autre mot-clé ou réinitialisez les filtres pour afficher l'ensemble des réponses."}
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="px-4 py-2 rounded-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 text-xs font-bold transition-colors"
            >
              {t.faq.resetSearch}
            </button>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-4">
            {filteredFaqs.map((faq) => {
              const isOpen = !!openItems[faq.id];
              const questionText = faq.question?.[currentLang] || faq.question?.fr;
              const answerText = faq.answer?.[currentLang] || faq.answer?.fr;

              return (
                <div
                  key={faq.id}
                  className={`rounded-sm border transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700'
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                  }`}
                >
                  {/* Accordion Question Header */}
                  <button
                    onClick={() => toggleItem(faq.id)}
                    className="w-full p-4 sm:p-5 text-left rtl:text-right flex items-start justify-between gap-4 select-none focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <div className="space-y-1.5 flex-grow">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-md bg-cyan-500/10 dark:bg-cyan-500/20 text-cyan-800 dark:text-cyan-300 text-[10px] font-bold uppercase tracking-wider border border-cyan-500/30">
                          {getCategoryLabel(faq.category)}
                        </span>
                        {faq.popular && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 text-[10px] font-bold border border-amber-500/30">
                            <Sparkles className="w-2.5 h-2.5 text-amber-500" />
                            <span>{t.faq.popularBadge}</span>
                          </span>
                        )}
                      </div>

                      <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white font-montserrat leading-snug">
                        {questionText}
                      </h3>
                    </div>

                    <div className={`p-1.5 transition-transform duration-200 shrink-0 ${
                      isOpen 
                        ? 'text-slate-900 dark:text-white rotate-180' 
                        : 'text-slate-500 dark:text-slate-400'
                    }`}>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>

                  {/* Accordion Answer Content */}
                  {isOpen && (
                    <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-4 text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed text-left rtl:text-right">
                      <div className="whitespace-pre-line pt-2">
                        {answerText}
                      </div>

                      {/* Tags */}
                      {faq.tags && faq.tags.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <span className="text-[11px] text-slate-400 me-1">
                            {currentLang === 'ar' ? 'الكلمات الدلالية :' : currentLang === 'en' ? 'Keywords:' : 'Mots-clés :'}
                          </span>
                          {faq.tags.map((tag, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery(tag);
                              }}
                              className="px-2 py-0.5 rounded-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-[11px] text-slate-600 dark:text-slate-400 transition-colors border border-slate-200 dark:border-slate-700"
                            >
                              #{tag}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Callout Banner */}
        <div className="mt-14 max-w-4xl mx-auto rounded-3xl p-6 sm:p-8 bg-gradient-to-r from-slate-900 via-[#09222E] to-slate-900 text-white border border-cyan-500/30 shadow-xl relative overflow-hidden">
          
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            
            <div className="space-y-2 text-center md:text-left rtl:md:text-right">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-cyan-300 uppercase tracking-wider">
                <MessageSquare className="w-3.5 h-3.5 text-cyan-400" />
                <span>
                  {currentLang === 'ar' ? 'المساعدة وتأطير المهام' : currentLang === 'en' ? 'Support & Mission Scoping' : 'Assistance & Cadrage de Mission'}
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-montserrat text-white">
                {t.faq.ctaTitle}
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 w-[90%] lg:w-[50%] max-w-none">
                {t.faq.ctaSubtitle}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
              {onNavigateToContact && (
                <button
                  onClick={() => onNavigateToContact()}
                  className="w-full sm:w-auto px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 transition-all hover:scale-105 active:scale-95"
                >
                  <span>{t.faq.ctaButton}</span>
                  <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                </button>
              )}

              {onNavigateToVerify && (
                <button
                  onClick={onNavigateToVerify}
                  className="w-full sm:w-auto px-4 py-3 rounded-xl bg-[#061822] hover:bg-[#0C2B38] text-slate-200 hover:text-white border border-cyan-500/40 text-xs font-semibold flex items-center justify-center gap-2 transition-all"
                >
                  <ShieldCheck className="w-4 h-4 text-cyan-400" />
                  <span>{t.faq.ctaVerify}</span>
                </button>
              )}
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
